import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Toaster } from "@bindery/ui/components/ui/sonner";
import { AdPrivacyNotice } from "@/components/ad-privacy-notice";
import { AppNav } from "@/components/app-nav";
import { LibraryPage } from "@/reader/library-page";
import { AboutPage } from "@/about/about-page";
import { TeacherPage } from "@/classroom/teacher-page";
import { PurchaserPage } from "@/classroom/purchaser-page";
import { PrivacyPage } from "@/about/privacy-page";
import { ReadingView } from "@/reader/reading-view";
import { PurchaseComplete } from "@/purchase/complete";
import { SubscribePage } from "@/subscribe/subscribe-page";
import { AuthCallback } from "@/auth/auth-callback";
import { EngagementTracker } from "@/lib/engagement/engagement-tracker";

// Lazy so the admin console (and, soon, its Shiki/tokenizer weight) never
// ships in the anonymous reader's bundle — an anonymous reader's path never
// touches Convex (@/lib/corpus.ts:1-5) and shouldn't pay for the console that
// does. (`AppNav` asks Convex for `admins.myRole`, but only once a session is
// confirmed, so the anonymous reading path is still Convex-free.)
const AdminPage = lazy(() =>
  import("@/admin/admin-page").then((m) => ({ default: m.AdminPage })),
);

/** Full-page loading state while the admin chunk downloads, matching AdminPanel's own "Checking access…" spinner. */
function AdminPageFallback() {
  return (
    <>
      <AppNav variant="floating" />
      <main className="mx-auto flex min-h-svh max-w-md flex-col items-center justify-center gap-5 px-6 text-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </main>
    </>
  );
}

/**
 * The shell for pages that have no header of their own: it hangs `AppNav` in
 * the top-right corner above the matched page.
 *
 * Surfaces that DO own a header are deliberately NOT under this layout — the
 * reader and the membership page render `<AppNav variant="inline">` inside
 * their own toolbar instead, so the site controls are part of that row rather
 * than floating over it. That overlap (and the `pr-16` reservations papering
 * over it) is what this split exists to end.
 */
function ChromeLayout() {
  return (
    <>
      <AppNav variant="floating" />
      <Outlet />
    </>
  );
}

function App({ authError }: { authError: string | null }) {
  // `authError` is captured at boot (main.tsx), before the app renders, so a
  // failed exchange survives even if the matched route (`/auth/callback`)
  // immediately navigates the URL elsewhere (review MINOR finding 6). It fires
  // HERE, above the router, so that crossing between the two layout groups
  // below can never remount the effect and toast the same failure twice.
  useEffect(() => {
    if (authError !== null) {
      toast.error("Sign-in failed", { description: authError });
    }
  }, [authError]);

  return (
    <>
      <BrowserRouter>
        {/* Inside the router so it can watch navigation, outside `Routes` so a
            route change does not remount it. Renders nothing. */}
        <EngagementTracker />
        {/* Renders nothing; fires the one-time ad-arrival privacy disclosure
            once the tracker above has a server-acknowledged tier stored (the
            ack lands async, so the toast shows on the following navigation). */}
        <AdPrivacyNotice />
        <Routes>
          {/* Headers of their own — see `ChromeLayout`. `:unit` is a chapter
              number (all digits) or a matter slug (PD-2); ReadingView
              dispatches on the shape. */}
          <Route path="/read/:bookSlug/:unit" element={<ReadingView />} />
          {/* Plans + the reader's own membership, one page: arriving from a
              spent allowance or the paywall, the question is always both
              halves at once. */}
          <Route path="/subscribe" element={<SubscribePage />} />
          {/* Both segments optional: bare `/admin` still works and resolves to
              the first configured book's first unit, while
              `/admin/peter-pan/3?loc=b012` is a shareable deep link straight to
              a paragraph. `:unit` has the same shape as the reader's — a
              chapter number or a matter slug — and both routes resolve it
              through `convex/model/unitOrder.ts`.

              Out here rather than under `ChromeLayout` because the two
              workbenches behind this route own full-height headers of their
              own; the console shell behind the same route does not, so
              `AdminPanel` hangs the floating variant itself for its own
              states. This route is the one place the choice is made per-STATE
              rather than per-route. */}
          <Route
            path="/admin/:bookSlug?/:unit?"
            element={
              <Suspense fallback={<AdminPageFallback />}>
                <AdminPage />
              </Suspense>
            }
          />

          <Route element={<ChromeLayout />}>
            <Route path="/" element={<LibraryPage />} />
            <Route path="/about" element={<AboutPage />} />
            {/* The classroom offer (docs/plan/ela-classroom-extension.md).
                The teacher page is a public pitch and gates only its actions;
                `/classroom/:token` is what a teacher's purchasing
                decision-maker opens, and is public by construction — the token
                is the only thing standing in for a session there. */}
            <Route path="/for-teachers/odyssey" element={<TeacherPage />} />
            {/* The bare route predates the per-book path and may be printed or
                shared; keep it landing somewhere until it becomes an index. */}
            <Route
              path="/for-teachers"
              element={<Navigate to="/for-teachers/odyssey" replace />}
            />
            <Route path="/classroom/:token" element={<PurchaserPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            {/* checkout.sara.ai return target (plan 10 §3): confirms + unlocks.
                Serves BOTH lanes — `?kind=subscription` confirms a signup
                instead of a one-time order. */}
            <Route path="/purchase/complete" element={<PurchaseComplete />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </>
  );
}

/** Minimal not-found page, rendered under `ChromeLayout` like every other
 *  headerless route. */
function NotFound() {
  return (
    <main className="mx-auto flex min-h-svh max-w-2xl flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-muted-foreground font-serif">Page not found.</p>
    </main>
  );
}

export default App;
